import { useState, type ReactElement } from 'react'

import { IconExChevron } from '@/components/ui/icons'
import { cn } from '@/lib/utils'
import { ProductRow } from '@/packages/explore/components/ProductRow'
import { ProductTile } from '@/packages/explore/components/ProductTile'
import { useLocalize } from '@/packages/explore/lib/localize'
import type { ExploreLayout, ProductGroup } from '@/packages/explore/types'

/**
 * The `layout` grid — six columns on the 700px panel, four from 600px and
 * three below, with 24px between the rows (Figma 2267:290759 · 291292 ·
 * 291939).
 */
export const EXPLORE_GRID =
  'grid w-full grid-cols-3 items-start gap-y-6 sm:grid-cols-4 md:grid-cols-6'

interface ExploreGroupProps {
  group: ProductGroup
  layout: ExploreLayout
  onNavigate?: (() => void) | undefined
  nodeId?: string
}

/**
 * Figma `panel-list` (2267:290755) — a 12/16 title with its caret, then the
 * products as tiles or as rows, 12px below it.
 */
export function ExploreGroup({
  group,
  layout,
  onNavigate,
  nodeId,
}: ExploreGroupProps): ReactElement {
  const [collapsed, setCollapsed] = useState(false)
  const localize = useLocalize()

  return (
    <section data-node-id={nodeId} data-name="panel-list" className="flex w-full flex-col gap-3">
      <button
        type="button"
        data-name="title"
        aria-expanded={!collapsed}
        onClick={() => {
          setCollapsed((value) => !value)
        }}
        className="flex cursor-pointer items-center self-start"
      >
        <span className="hub-meta-md text-list-text">{localize(group.title)}</span>
        <IconExChevron
          className={cn(
            'text-widget-secondary size-4 shrink-0 transition-transform',
            collapsed && 'rotate-180',
          )}
          aria-hidden="true"
        />
      </button>

      {!collapsed &&
        (layout === 'grid' ? (
          <div data-name="layout" className={EXPLORE_GRID}>
            {group.products.map((product) => (
              <ProductTile key={product.id} product={product} onNavigate={onNavigate} />
            ))}
          </div>
        ) : (
          <div data-name="results" className="flex w-full flex-col">
            {group.products.map((product) => (
              <ProductRow key={product.id} product={product} onNavigate={onNavigate} />
            ))}
          </div>
        ))}
    </section>
  )
}
